/**
 * CLA Theme Configuration
 * Clifton Larson Allen brand colors and styling for Material-UI
 */

import { createTheme } from '@mui/material/styles';
import type { CSSProperties } from 'react';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    h2Headline: CSSProperties;
    h2SectionHeader: CSSProperties;
  }

  interface TypographyVariantsOptions {
    h2Headline?: CSSProperties;
    h2SectionHeader?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h2Headline: true;
    h2SectionHeader: true;
  }
}

// CLA Brand Colors
const claColors = {
  primary: '#00A9B5', // Teal
  secondary: '#1C3654', // Navy
  accent: '#F5F5F5', // Light gray
  success: '#27AE60',
  warning: '#F39C12',
  error: '#E74C3C',
  info: '#3498DB',
};

export const claTheme = createTheme({
  palette: {
    primary: {
      main: claColors.secondary,
      light: '#2C5282',
      dark: '#0F1B2E',
      contrastText: '#fff',
    },
    secondary: {
      main: claColors.primary,
      light: '#00D4E0',
      dark: '#008999',
      contrastText: '#fff',
    },
    success: {
      main: claColors.success,
    },
    warning: {
      main: claColors.warning,
    },
    error: {
      main: claColors.error,
    },
    info: {
      main: claColors.info,
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      marginTop: 24,
      fontSize: '2rem',
      fontWeight: 700,
      color: claColors.secondary,
    },
    h2: {
      marginTop: 24,
      fontSize: 34,
      fontWeight: 700,
      color: claColors.secondary,
    },
    h2Headline: {
      marginTop: '1.25rem',
      fontSize: 34,
      fontWeight: 700,
      color: claColors.secondary,
      lineHeight: 1.15,
    },
    h2SectionHeader: {
      marginTop: 24,
      fontSize: 34,
      fontWeight: 700,
      color: claColors.accent,
      backgroundColor: claColors.secondary,
      lineHeight: 1.15,
      padding: '0.5rem 1rem',
    },
    h3: {
      marginTop: 24,
      fontSize: 24,
      fontWeight: 600,
      color: claColors.secondary,
    },
    h4: {
      marginTop: 24,
      fontSize: 18,
      fontWeight: 600,
      color: claColors.secondary,
    },
    h5: {
      marginTop: 24,
      fontSize: 16,
      fontWeight: 600,
      color: claColors.secondary,
    },
    h6: {
      marginTop: 24,
      fontSize: 16,
      fontWeight: 600,
      color: claColors.secondary,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      color: claColors.secondary,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: 16,
    },
  },
  components: {
    MuiDivider: {
      styleOverrides: {
        root: {
          '&.horizontal-rule': {
            margin: '2rem 0',
            borderColor: '#c7d0d8',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          '&.style-guide-table': {
            backgroundColor: '#eeeeed',
            border: '2px solid #fff',
            '& .MuiTableHead-root': {
              backgroundColor: '#fcd17b',
              '& .MuiTableCell-root:not(:last-child)': {
                borderRight: '2px solid #fff',
              },
            },
            '& .MuiTableRow-root': {
              border: '2px solid #fff',
            },
          },
          '&.case-study-table': {
            '& .MuiTableCell-root': {
              border: '5px solid #fff',
              '&.left': {
                backgroundColor: '#7dd2d3',
              },
              '&.middle': {
                backgroundColor: '#e2e868',
              },
              '&.right': {
                backgroundColor: '#f5f7d1',
              },
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.byline': {
            fontStyle: 'italic',
            margin: '20px 0',
          },
          '&.quote-author': {
            fontStyle: 'normal',
            fontWeight: 550,
          },
          '&.panel-subheading': {
            marginTop: 24,
          },
          '&.image-caption': {
            display: 'block',
            marginTop: 8,
            color: '#52606d',
          },
          '&.content-callout': {
            padding: 16,
            borderLeft: '4px solid #00A9B5',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          fontWeight: 700,
          transition: 'all 0.3s ease',
          border: `2px solid transparent`,
          padding: '7px 24px',
        },
        containedPrimary: {
          backgroundColor: claColors.secondary,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            backgroundColor: 'transparent',
            color: claColors.secondary,
            borderColor: claColors.secondary,
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          },
        },
        outlinedPrimary: {
          borderColor: claColors.secondary,
          color: claColors.secondary,
          '&:hover': {
            backgroundColor: claColors.secondary,
            color: 'white',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: 4,
          backgroundColor: '#e0e0e0',
        },
        bar: {
          borderRadius: 4,
          backgroundColor: claColors.secondary,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: claColors.secondary,
            },
            '&.Mui-focused fieldset': {
              borderColor: claColors.secondary,
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: claColors.secondary,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: claColors.secondary,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: claColors.primary,
          height: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          color: claColors.secondary,
          '&.Mui-selected': {
            color: claColors.primary,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: claColors.secondary,
          fontWeight: 700,
          textDecoration: 'none',
          fontStyle: 'normal',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: claColors.secondary,
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: claColors.secondary,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: '0 0px 0px rgba(0,0,0,0)',
          '&.article-cta': {
            backgroundColor: '#eaefa2',
            padding: 20,
          },
          '&.detail-cta': {
            backgroundColor: '#c2eaea',
            padding: 20,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 3,
            flexWrap: 'wrap',
          },
          '&.article-key-insights': {
            backgroundColor: '#e9f6f7',
            padding: 20,
            '& ul': {
              marginLeft: 20,
              fontWeight: 600,
            },
          },
          '&.style-guide-panel': {
            padding: 24,
            backgroundColor: '#f0efef',

            '&.right-align': {
              maxWidth: '45%',
              minWidth: '35%',
              float: 'right',
              margin: '0 0 1em 1em',
            },
            '&.left-align': {
              maxWidth: '45%',
              minWidth: '35%',
              float: 'left',
              margin: '0 1em 1em 0',
            },
          },
          '&.article-update': {
            padding: '0.5rem',
            backgroundColor: '#dad8d7',
            fontStyle: 'italic',
            "& b": {
              fontStyle: 'normal',
            },
          },
          '&.accordion-panel': {
            padding: 16,
          },
          '&.style-guide-color-swatch': {
            padding: 12,
            minHeight: 48,
          },
        },
      },
    },
  },
});

export default claTheme;
